import React, { useState, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  X, Palette, Image as ImageIcon, Upload, Check, Save, Loader, LayoutTemplate,
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { INVOICE_TEMPLATES, ACCENT_SWATCHES } from './templates';
import { DEFAULT_BRANDING, persistBranding } from './branding';
import InvoicePreview from './InvoicePreview';

// Facture d'exemple pour visualiser le rendu du modèle global.
const SAMPLE_INVOICE = (() => {
  const items = [
    { description: 'Produit / service A', quantity: 2, unitPrice: 25 },
    { description: 'Produit / service B', quantity: 1, unitPrice: 40 },
    { description: 'Frais de service', quantity: 1, unitPrice: 15 },
  ];
  const subtotal = items.reduce((s, it) => s + it.quantity * it.unitPrice, 0);
  const taxRate = 0.2;
  const taxAmount = subtotal * taxRate;
  const now = new Date();
  const due = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return {
    invoiceNumber: 'INV-2024-0001',
    issueDate: now.toISOString(),
    dueDate: due.toISOString(),
    clientName: 'Client Exemple',
    clientEmail: 'client@exemple.com',
    clientPhone: '+237 6XX XXX XXX',
    items,
    subtotal,
    taxRate,
    taxAmount,
    discount: 0,
    total: subtotal + taxAmount,
    currency: 'EUR',
    notes: 'Merci pour votre confiance. Paiement à réception via le lien fourni.',
  };
})();

/**
 * Éditeur du MODÈLE DE FACTURE GLOBAL de l'entreprise.
 * Défini une seule fois, il s'applique à toutes les factures (le PDF est généré
 * côté backend à partir de ces réglages). On visualise ici le rendu sur un exemple.
 */
export default function InvoiceTemplateSettingsModal({ branding, onClose, onSaved }) {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const init = { ...DEFAULT_BRANDING, ...(branding || {}) };
  const [templateId, setTemplateId] = useState(init.templateId);
  const [brandColor, setBrandColor] = useState(init.brandColor);
  const [logoUrl, setLogoUrl] = useState(init.logoUrl);
  const [companyName, setCompanyName] = useState(init.companyName);
  const [companyAddress, setCompanyAddress] = useState(init.companyAddress);
  const [companyPhone, setCompanyPhone] = useState(init.companyPhone);
  const [companyEmail, setCompanyEmail] = useState(init.companyEmail);

  const handleLogoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Veuillez choisir une image'); return; }
    if (file.size > 1024 * 1024) { toast.error('Logo trop lourd (max 1 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result);
    reader.readAsDataURL(file);
  }, [toast]);

  const brandingData = useMemo(() => ({
    logoUrl, companyName, companyAddress, companyPhone, companyEmail, brandColor,
  }), [logoUrl, companyName, companyAddress, companyPhone, companyEmail, brandColor]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    const form = { templateId, brandColor, logoUrl, companyName, companyAddress, companyPhone, companyEmail };
    setSaving(true);
    try {
      await persistBranding(form);
      toast.success('Modèle appliqué à toutes vos factures', { title: 'Modèle enregistré' });
      onSaved?.(form);
    } catch (err) {
      // Le cache local est déjà enregistré par persistBranding → on applique quand même.
      console.warn('⚠️ Sauvegarde backend du modèle indisponible:', err);
      toast.info("Modèle enregistré localement. Le backend doit être mis à jour pour le conserver côté serveur.");
      onSaved?.(form);
    } finally {
      setSaving(false);
    }
  }, [saving, templateId, brandColor, logoUrl, companyName, companyAddress, companyPhone, companyEmail, onSaved, toast]);

  const inputCls =
    'w-full bg-primary-dark text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-green placeholder-text-gray';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="bg-gradient-to-br from-primary-card to-primary-card/90 rounded-3xl max-w-5xl w-full max-h-[92vh] overflow-hidden border border-primary-sidebar/50 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-dark/50 to-primary-sidebar/50 p-4 sm:p-6 border-b border-primary-sidebar/30 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-accent-green to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-accent-green/25 flex-shrink-0">
                <LayoutTemplate className="text-black" size={28} />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-white truncate">Modèle de facture</h2>
                <p className="text-text-gray text-sm">
                  Définissez l'identité de vos factures — appliquée automatiquement à toutes les factures.
                </p>
              </div>
            </div>
            <motion.button
              onClick={onClose}
              className="p-3 hover:bg-primary-dark/50 rounded-xl text-text-gray hover:text-white transition-all flex-shrink-0"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              <X size={24} />
            </motion.button>
          </div>
        </div>

        {/* Contenu : réglages (gauche) + aperçu (droite) */}
        <div className="overflow-y-auto flex-1 grid grid-cols-1 lg:grid-cols-2">
          {/* Réglages */}
          <div className="p-4 sm:p-6 space-y-5 border-b lg:border-b-0 lg:border-r border-primary-sidebar/30">
            {/* Logo + société */}
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                <ImageIcon className="text-accent-green" size={18} /> Logo & entreprise
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex flex-col items-center justify-center gap-2 bg-primary-dark/30 rounded-xl p-4 border border-dashed border-primary-sidebar/60">
                  <div className="w-20 h-20 rounded-xl bg-primary-card flex items-center justify-center overflow-hidden">
                    {logoUrl ? (
                      <img src={logoUrl} alt="logo" className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon className="text-text-gray" size={28} />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs px-3 py-1.5 rounded-lg bg-accent-green/15 border border-accent-green/30 text-accent-green font-semibold hover:bg-accent-green/25 transition-all flex items-center gap-1"
                    >
                      <Upload size={12} /> {logoUrl ? 'Changer' : 'Logo'}
                    </button>
                    {logoUrl && (
                      <button
                        type="button"
                        onClick={() => setLogoUrl('')}
                        className="text-xs px-2 py-1.5 rounded-lg text-red-400 hover:bg-red-500/15 transition-all"
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                  <span className="text-[10px] text-text-gray text-center">PNG/JPG · max 1 Mo</span>
                </div>

                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Nom de l'entreprise" className={inputCls} />
                  <input type="text" value={companyPhone} onChange={(e) => setCompanyPhone(e.target.value)} placeholder="Téléphone" className={inputCls} />
                  <input type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} placeholder="Email" className={inputCls} />
                  <input type="text" value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} placeholder="Adresse" className={inputCls} />
                </div>
              </div>
            </div>

            {/* Modèles */}
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                <LayoutTemplate className="text-accent-green" size={18} /> Modèle
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {INVOICE_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplateId(t.id)}
                    className={`text-left p-3 rounded-xl border transition-all ${
                      templateId === t.id
                        ? 'border-accent-green bg-accent-green/10'
                        : 'border-primary-sidebar/60 bg-primary-dark/30 hover:border-primary-sidebar'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-sm font-semibold">{t.name}</span>
                      {templateId === t.id && <Check size={14} className="text-accent-green" />}
                    </div>
                    <span className="text-text-gray text-[11px]">{t.description}</span>
                    <div className="flex gap-1 mt-2">
                      <span className="w-6 h-2 rounded-full" style={{ background: '#22323F' }} />
                      <span className="w-10 h-2 rounded-full" style={{ background: brandColor || t.defaultAccent }} />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Couleur */}
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                <Palette className="text-accent-green" size={18} /> Couleur d'accent
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                {ACCENT_SWATCHES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setBrandColor(c)}
                    className={`w-7 h-7 rounded-full border-2 transition-all ${brandColor === c ? 'border-white scale-110' : 'border-transparent'}`}
                    style={{ background: c }}
                    title={c}
                  />
                ))}
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="w-7 h-7 rounded cursor-pointer bg-transparent border border-primary-sidebar/60 ml-1"
                  title="Couleur personnalisée"
                />
              </div>
            </div>
          </div>

          {/* Aperçu */}
          <div className="p-4 sm:p-6 bg-[#0d1117]">
            <p className="text-text-gray text-xs mb-3">Aperçu (exemple)</p>
            <InvoicePreview templateId={templateId} invoice={SAMPLE_INVOICE} branding={brandingData} />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-primary-sidebar/30 flex items-center justify-end gap-3 flex-shrink-0 bg-primary-card/60">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-primary-dark text-white rounded-xl font-medium hover:bg-primary-sidebar transition-all"
          >
            Annuler
          </button>
          <motion.button
            onClick={handleSave}
            disabled={saving}
            whileHover={{ scale: saving ? 1 : 1.02 }}
            whileTap={{ scale: saving ? 1 : 0.98 }}
            className="px-5 py-2.5 bg-gradient-to-r from-accent-green to-emerald-500 text-black rounded-xl font-bold flex items-center gap-2 hover:shadow-lg hover:shadow-accent-green/25 transition-all disabled:opacity-50"
          >
            {saving ? <Loader size={18} className="animate-spin" /> : <Save size={18} />}
            {saving ? 'Enregistrement…' : 'Enregistrer le modèle'}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}
